import React from 'react';
import { RefreshCw, ShieldAlert, ShieldCheck } from 'lucide-react';
import { apiFetch } from '../shared/api/client';
import { Button, Card, Input } from '../shared/ui';
import { theme } from '../shared/config/theme';
import { useAuth } from '../app/providers/AuthProvider';
import MeasurementPanel from './MeasurementPanel';
import MobileActivityPanel from './MobileActivityPanel';
import CodeActivityPanel from './CodeActivityPanel';
import { ProviderCostCell, ProviderCostSummary } from './ProviderCost';
import AdminTabs, { AdminTabPanel, useAdminTabs } from './AdminTabs';
import AdminUserDetails from './AdminUserDetails';
import TrialUsersPanel from './TrialUsersPanel';

const PERIOD_OPTIONS = [
  { id: 'day', name: 'День' },
  { id: 'week', name: '7 дней' },
  { id: 'month', name: '30 дней' },
  { id: 'all', name: 'Весь период' },
  { id: 'custom', name: 'Свои даты' },
];

function todayYmd() {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return '';
  }
}

function shiftYmd(daysDelta) {
  try {
    const d = new Date(`${todayYmd()}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + Number(daysDelta || 0));
    return d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function planLabel(planCode) {
  const code = String(planCode || 'free').trim().toLowerCase();
  if (code === 'max') return 'Max';
  if (code === 'pro') return 'Pro';
  if (code === 'plus') return 'Plus';
  if (code === 'mini') return 'Mini';
  return code === 'free' ? 'Free' : code;
}

function formatGeneratedAt(value) {
  try {
    if (!value) return '';
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return String(value || '');
  }
}

function formatShortDate(value) {
  try {
    if (!value) return '-';
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value || '-');
  }
}

function formatSubscriptionPeriod(row) {
  const start = row?.current_period_start ? formatShortDate(row.current_period_start) : '-';
  const end = row?.current_period_end ? formatShortDate(row.current_period_end) : '-';
  if (start === '-' && end === '-') return '-';
  return `${start} -> ${end}`;
}

function paymentKindLabel(kind) {
  const value = String(kind || '').trim().toLowerCase();
  if (value === 'renewal') return '\u041f\u0440\u043e\u0434\u043b\u0435\u043d\u0438\u0435';
  if (value === 'extend') return 'Extend';
  if (value === 'upgrade') return '\u0410\u043f\u0433\u0440\u0435\u0439\u0434';
  if (value === 'trial_check') return 'Trial check';
  return '\u041e\u043f\u043b\u0430\u0442\u0430';
}

function paymentStatusLabel(status) {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'partially_refunded') return '\u0427\u0430\u0441\u0442\u0438\u0447\u043d\u044b\u0439 \u0432\u043e\u0437\u0432\u0440\u0430\u0442';
  if (value === 'refunded') return '\u0412\u043e\u0437\u0432\u0440\u0430\u0442';
  if (value === 'confirmed' || value === 'succeeded') return '\u041f\u043e\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043d';
  return status || '-';
}

function buildDashboardPath(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  const query = search.toString();
  return query ? `/analytics/admin/dashboard?${query}` : '/analytics/admin/dashboard';
}


export default function AdminDashboardPage() {
  const { logout } = useAuth();
  const { activeTab, visited, selectTab } = useAdminTabs();
  const hasSharedPeriod = activeTab !== 'marketing';
  const hasAIScope = activeTab === 'overview' || activeTab === 'mobile-activity';
  const [includeInternal, setIncludeInternal] = React.useState(false);
  const [includeTest, setIncludeTest] = React.useState(false);
  const [period, setPeriod] = React.useState('month');
  const [customFrom, setCustomFrom] = React.useState(() => shiftYmd(-29));
  const [customTo, setCustomTo] = React.useState(() => todayYmd());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [forbidden, setForbidden] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [reloadTick, setReloadTick] = React.useState(0);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const lastLoadedAt = React.useRef(0);

  React.useEffect(() => {
    if (loading) return undefined;
    const refreshOnReturn = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastLoadedAt.current < 30000) return;
      // Focus and visibilitychange can fire together; refresh only once.
      lastLoadedAt.current = Date.now();
      setReloadTick(value => value + 1);
    };
    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => {
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
  }, [loading]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      try {
        setLoading(true);
        setData(null);
        setError('');
        setForbidden(false);
        const params = { period, include_internal: includeInternal, include_test: includeTest };
        if (period === 'custom') {
          params.date_from = customFrom;
          params.date_to = customTo;
        }
        const response = await apiFetch(buildDashboardPath(params), { cache: 'no-store' });
        const payload = await response.json();
        if (!cancelled) {
          setData(payload || null);
          lastLoadedAt.current = Date.now();
        }
      } catch (err) {
        if (cancelled) return;
        const msg = String(err?.message || '').trim() || 'Не удалось загрузить admin dashboard';
        setError(msg);
        setForbidden(err?.status === 403 || /admin access/i.test(msg));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [period, customFrom, customTo, reloadTick, includeInternal, includeTest]);

  React.useEffect(() => { setSelectedUser(null); }, [includeInternal, includeTest]);

  const users = Array.isArray(data?.users) ? data.users : [];
  const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
  const payments = Array.isArray(data?.payments) ? data.payments : [];

  if (forbidden) {
    return (
      <Card className="max-w-3xl mx-auto mt-10" padding="lg">
        <div className="flex items-start gap-4">
          <div
            className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(220,38,38,0.12)', color: '#dc2626' }}
          >
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold mb-2" style={{ color: theme.text }}>
              Доступ запрещён
            </h1>
            <p className="text-sm mb-5" style={{ color: theme.textSoft }}>
              Вход в admin.noesa.ai разрешён только для <b>arthuroberud@gmail.com</b>.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => window.location.assign('https://noesa.ai')}>
                На основной сайт
              </Button>
              <Button variant="ghost" onClick={logout}>
                Выйти
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 sm:px-8">
      <div className="flex flex-col gap-6">
        <Card padding="lg" className="overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full mb-4" style={{ background: 'rgba(30,102,245,0.10)', color: theme.brand }}>
                <ShieldCheck className="h-3.5 w-3.5" />
                owner-only dashboard
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3" style={{ color: theme.text }}>
                Админка Noesa
              </h1>
              <p className="text-sm sm:text-base leading-7" style={{ color: theme.textSoft }}>
                {activeTab === 'overview' ? 'Noesa AI: сайт и мобильное приложение вместе. Активность и расходы Noesa Code — отдельно.' :
                  activeTab === 'noesa-code' ? 'Активность, модели и расходы десктопного приложения.' :
                  activeTab === 'mobile-activity' ? 'Только действия iOS и Android, независимо от места регистрации аккаунта.' :
                  'У этого раздела собственные фильтры периода и кнопка обновления.'}
              </p>
              {hasSharedPeriod && <p className="text-xs mt-4" style={{ color: theme.textSoft }}>
                Последнее обновление: {formatGeneratedAt(data?.generated_at) || '—'}
              </p>}
            </div>

            <div className={hasSharedPeriod ? 'lg:min-w-[360px]' : ''}>
              {hasSharedPeriod && <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setPeriod(option.id)}
                    className="rounded-xl px-3 py-2 text-sm font-medium transition-all"
                    style={{
                      background: period === option.id ? 'var(--accent-bg)' : theme.bgSoft,
                      color: period === option.id ? 'var(--accent-text)' : theme.text,
                      border: `1px solid ${period === option.id ? theme.brand : theme.border}`,
                    }}
                  >
                    {option.name}
                  </button>
                ))}
              </div>

              {period === 'custom' && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Input
                    type="date"
                    label="Дата от"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                  <Input
                    type="date"
                    label="Дата до"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
              )}
              </>}

              <div className="flex flex-wrap gap-3 mt-4">
                {hasSharedPeriod && <Button
                  variant="outline"
                  onClick={() => setReloadTick((n) => n + 1)}
                  loading={loading}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Обновить
                </Button>}
                <Button variant="ghost" onClick={logout}>
                  Выйти
                </Button>
              </div>
            </div>
          </div>
          <AdminTabs activeTab={activeTab} onSelect={selectTab} />
          {hasAIScope && <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={includeInternal} onChange={e => setIncludeInternal(e.target.checked)} />Показать внутренние аккаунты</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={includeTest} onChange={e => setIncludeTest(e.target.checked)} />Показать тестовые события</label>
            <p className="w-full text-xs" style={{ color: theme.textSoft }}>Общие фильтры «Обзора» и мобильного приложения. По умолчанию — внешние аккаунты и production. Тестовые события не меняют финансовые записи.</p>
          </div>}
        </Card>

        <AdminTabPanel id="mobile-activity" activeTab={activeTab}>
          {visited['mobile-activity'] && data && !loading && !error && <MobileActivityPanel active={activeTab === 'mobile-activity'}
            dateFrom={data.date_from} dateTo={data.date_to} asOf={data.activity_scope?.as_of}
            includeInternal={includeInternal} includeTest={includeTest} onSelectUser={setSelectedUser} onRefresh={() => setReloadTick(n => n + 1)} />}
        </AdminTabPanel>
        <AdminTabPanel id="marketing" activeTab={activeTab}>
          {visited.marketing && <MeasurementPanel active={activeTab === 'marketing'} />}
        </AdminTabPanel>

        {hasSharedPeriod && loading && <p role="status">Загружаем показатели…</p>}
        {hasSharedPeriod && error && !forbidden && (
          <Card padding="md" style={{ borderColor: 'rgba(220,38,38,0.22)', background: 'rgba(254,242,242,0.75)' }}>
            <div className="text-sm" role="alert" style={{ color: '#991b1b' }}>
              {error}
            </div>
          </Card>
        )}

        <AdminTabPanel id="noesa-code" activeTab={activeTab}>
          {data && !loading && !error && <CodeActivityPanel data={data} loading={loading} onSelectUser={setSelectedUser} />}
        </AdminTabPanel>
        <AdminTabPanel id="overview" activeTab={activeTab}>
        {data && !loading && !error && <>
        {data.activity_scope && <Card padding="md"><p className="text-sm">
          Активность: сайт + мобильное приложение, без Code. Один аккаунт считается один раз. ИИ-черновики дневника учитываются только после принятия пользователем.
          Регистрация и открытие приложения не считаются использованием функций.
          {includeTest ? ' Включены тестовые события.' : ' Только production-события.'}
        </p><p className="text-xs mt-2" style={{ color: theme.textSoft }}>
          Подробный журнал с {formatGeneratedAt(data.activity_scope.first_received_at) || 'неизвестной даты'}; ранняя история неполная.
          Событий без определённой платформы: {data.activity_scope.totals.unknown_platform_events}. Не приписываем их сайту или телефону.
          Старые записи в таблице пользователей ниже — отдельный срез сохранившихся объектов, не источник DAU/WAU/MAU.
        </p></Card>}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <Card key={metric.key} padding="lg">
              <div className="flex flex-col gap-3">
                <div>
                  <div className="text-sm font-semibold" style={{ color: theme.text }}>
                    {metric.title}
                  </div>
                  <div className="text-xs leading-5 mt-1" style={{ color: theme.textSoft }}>
                    Как считаем: {metric.description}
                  </div>
                </div>
                <div className="text-3xl font-semibold tracking-tight" style={{ color: theme.text }}>
                  {metric.display_value}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <TrialUsersPanel users={users} onSelectUser={setSelectedUser} />

        <Card padding="lg">
          <div className="flex flex-col gap-3 mb-5">
            <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
              {'\u041f\u043b\u0430\u0442\u0435\u0436\u0438'}
            </h2>
            <p className="text-sm" style={{ color: theme.textSoft }}>Оплаты сайта по общим подпискам аккаунта. Не отдельная выручка Code. Оплаты магазинов — в мобильной вкладке; покупка на сайте не превращается в оплату App Store при входе с телефона.</p>
            <p className="text-sm leading-6" style={{ color: theme.textSoft }}>
              {'\u0412\u044b\u0440\u0443\u0447\u043a\u0430 \u0441\u0447\u0438\u0442\u0430\u0435\u0442\u0441\u044f \u043f\u043e net: gross \u043c\u0438\u043d\u0443\u0441 \u0432\u043e\u0437\u0432\u0440\u0430\u0442. \u0427\u0430\u0441\u0442\u0438\u0447\u043d\u044b\u0435 \u0432\u043e\u0437\u0432\u0440\u0430\u0442\u044b \u043f\u043e\u043c\u0435\u0447\u0435\u043d\u044b \u043e\u0442\u0434\u0435\u043b\u044c\u043d\u043e.'}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ color: theme.textSoft, borderBottom: `1px solid ${theme.border}` }}>
                  <th className="text-left py-3 pr-4 font-medium">Email</th>
                  <th className="text-left py-3 pr-4 font-medium">{'\u0422\u0430\u0440\u0438\u0444'}</th>
                  <th className="text-left py-3 pr-4 font-medium">{'\u0422\u0438\u043f'}</th>
                  <th className="text-left py-3 pr-4 font-medium">{'\u0421\u0442\u0430\u0442\u0443\u0441'}</th>
                  <th className="text-right py-3 px-2 font-medium">Gross</th>
                  <th className="text-right py-3 px-2 font-medium">Refund</th>
                  <th className="text-right py-3 px-2 font-medium">Net</th>
                  <th className="text-left py-3 px-2 font-medium">Payment ID</th>
                  <th className="text-left py-3 pl-4 font-medium">{'\u0414\u0430\u0442\u0430'}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((row) => (
                  <tr key={`${row.payment_id || row.checkout_id || row.email}.${row.paid_at || ''}`} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td className="py-3 pr-4 align-top">
                      <button type="button" className="admin-user-link" style={{ color: theme.text }} onClick={() => setSelectedUser(row)} aria-label={`Открыть пользователя ${row.email}`}>{row.email}</button>
                    </td>
                    <td className="py-3 pr-4 align-top" style={{ color: theme.text }}>{planLabel(row.plan_code)}</td>
                    <td className="py-3 pr-4 align-top" style={{ color: theme.text }}>{paymentKindLabel(row.kind)}</td>
                    <td className="py-3 pr-4 align-top">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                        style={{
                          background: row.is_partially_refunded ? 'rgba(245,158,11,0.14)' : 'rgba(16,185,129,0.12)',
                          color: row.is_partially_refunded ? '#92400e' : '#047857',
                        }}
                      >
                        {paymentStatusLabel(row.status)}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.gross_display}</td>
                    <td className="py-3 px-2 text-right" style={{ color: row.refunded_amount > 0 ? '#92400e' : theme.textSoft }}>
                      {row.refunded_display}
                    </td>
                    <td className="py-3 px-2 text-right font-semibold" style={{ color: theme.text }}>{row.net_display}</td>
                    <td className="py-3 px-2 align-top" style={{ color: theme.textSoft }}>
                      <span className="font-mono text-xs">{row.payment_id || row.checkout_id || '-'}</span>
                      {row.is_reconciled && (
                        <span className="ml-2 text-xs" style={{ color: '#92400e' }}>reconciled</span>
                      )}
                    </td>
                    <td className="py-3 pl-4 align-top whitespace-nowrap" style={{ color: theme.textSoft }}>
                      {formatShortDate(row.paid_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && payments.length === 0 && (
            <div className="text-sm py-8" style={{ color: theme.textSoft }}>
              {'\u041f\u043b\u0430\u0442\u0435\u0436\u0438 \u0437\u0430 \u0432\u044b\u0431\u0440\u0430\u043d\u043d\u044b\u0439 \u043f\u0435\u0440\u0438\u043e\u0434 \u043d\u0435 \u043d\u0430\u0439\u0434\u0435\u043d\u044b.'}
            </div>
          )}
        </Card>

        <Card padding="lg">
          <div className="flex flex-col gap-3 mb-5">
            <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
              Пользователи
            </h2>
            <p className="text-sm leading-6" style={{ color: theme.textSoft }}>
              Показатели активности и расходы API считаются за выбранный период. Данные расходов доступны с 1 августа 2026 года.
              Это общая история аккаунтов Noesa AI: старые записи не всегда разделяют сайт и мобильное приложение. Подтверждённая мобильная активность — на отдельной вкладке.
              Расходы Noesa Code не включены. Резервы и ручные изменения лимитов не учитываются; неполные данные отмечены рядом с суммой.
              Дневник = записи в `records`, чат = пользовательские сообщения, календарь = созданные события, трекер = отметки в `tracker_marks`, аналитика = использования `analytics_*`.
              {' '}{'\u041c\u043e\u0437\u0433 = \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044f \u0432\u043e \u0432\u043a\u043b\u0430\u0434\u043a\u0435 \u043c\u043e\u0437\u0433\u0430, \u043a\u043e\u0442\u043e\u0440\u044b\u0435 \u043b\u043e\u0433\u0438\u0440\u0443\u044e\u0442\u0441\u044f \u043a\u0430\u043a `brain_*`.'}
            </p>
          </div>

          <ProviderCostSummary cost={data?.provider_cost} includeInternal={includeInternal} />
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr style={{ color: theme.textSoft, borderBottom: `1px solid ${theme.border}` }}>
                  <th className="text-left py-3 pr-4 font-medium">Email</th>
                  <th className="text-left py-3 pr-4 font-medium">Тариф</th>
                  <th className="text-left py-3 pr-4 font-medium">{'\u0421\u0442\u0430\u0442\u0443\u0441'}</th>
                  <th className="text-left py-3 pr-4 font-medium">{'\u041f\u0435\u0440\u0438\u043e\u0434'}</th>
                  <th className="text-right py-3 px-3 font-medium whitespace-nowrap" title="По московскому дню учёта. По полученным токенам и учтённым вызовам, без резервов. Неполные данные отмечены рядом с суммой.">
                    Расход API · USD
                  </th>
                  <th className="text-right py-3 px-2 font-medium">Дневник</th>
                  <th className="text-right py-3 px-2 font-medium">Чат</th>
                  <th className="text-right py-3 px-2 font-medium">Календарь</th>
                  <th className="text-right py-3 px-2 font-medium">Трекер</th>
                  <th className="text-right py-3 px-2 font-medium">Аналитика</th>
                  <th className="text-right py-3 px-2 font-medium">{'\u041c\u043e\u0437\u0433'}</th>
                  <th className="text-right py-3 pl-4 font-medium">Всего</th>
                </tr>
              </thead>
              <tbody>
                {users.map((row) => (
                  <tr key={`${row.email}.${row.created_at || ''}`} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td className="py-3 pr-4 align-top">
                      <button type="button" className="admin-user-link" style={{ color: theme.text }} onClick={() => setSelectedUser(row)} aria-label={`Открыть пользователя ${row.email}`}>{row.email}</button>
                      {row.is_internal && <div className="text-xs mt-1" style={{ color: theme.textSoft }}>Внутренний аккаунт</div>}
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
                        style={{
                          background: row.is_trial_active ? 'rgba(245,158,11,.12)' : row.is_paying ? 'rgba(16,185,129,0.12)' : theme.bgSoft,
                          color: row.is_trial_active ? '#92400e' : row.is_paying ? '#047857' : theme.textSoft,
                        }}
                      >
                        {planLabel(row.plan_code)}
                      </span>
                    </td>
                    <td className="py-3 pr-4 align-top" style={{ color: theme.textSoft }}>
                      {row.is_trial_active ? 'Пробный период' : row.subscription_status || '-'}
                    </td>
                    <td className="py-3 pr-4 align-top whitespace-nowrap" style={{ color: theme.textSoft }}>
                      {formatSubscriptionPeriod(row)}
                    </td>
                    <td className="py-3 px-3 text-right font-semibold whitespace-nowrap" style={{ color: theme.text }}>
                      <ProviderCostCell cost={row.stats?.provider_cost} />
                    </td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.diary ?? 0}</td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.chat ?? 0}</td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.calendar ?? 0}</td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.tracker ?? 0}</td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.analytics ?? 0}</td>
                    <td className="py-3 px-2 text-right" style={{ color: theme.text }}>{row.stats?.brain ?? 0}</td>
                    <td className="py-3 pl-4 text-right font-semibold" style={{ color: theme.text }}>
                      {row.stats?.total ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && users.length === 0 && (
            <div className="text-sm py-8" style={{ color: theme.textSoft }}>
              Пользователи не найдены.
            </div>
          )}
        </Card>
        </>}
        </AdminTabPanel>
        <AdminUserDetails user={selectedUser} includeTest={includeTest && activeTab !== 'noesa-code'} onClose={() => setSelectedUser(null)} />
      </div>
    </div>
  );
}
